/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { error } from '@instructure/console';
import { px } from '@instructure/ui-utils';
import { matchComponentTypes } from '@instructure/ui-react-utils';
import { Responsive } from '@instructure/ui-responsive';
import { TopNavBarActionItems } from "./TopNavBarActionItems/index.js";
import { TopNavBarBrand } from "./TopNavBarBrand/index.js";
import { TopNavBarItem } from "./TopNavBarItem/index.js";
import { TopNavBarLayout } from "./TopNavBarLayout/index.js";
import { TopNavBarMenuItems } from "./TopNavBarMenuItems/index.js";
import { TopNavBarUser } from "./TopNavBarUser/index.js";
import { TopNavBarBreadcrumb } from "./TopNavBarBreadcrumb/index.js";
import { TopNavBarContext } from "./TopNavBarContext.js";
import { allowedProps } from "./props.js";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
category: components
---
**/
class TopNavBar extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
  }
  get breakpoint() {
    return px(this.props.breakpoint);
  }
  render() {
    const _this$props = this.props,
      children = _this$props.children,
      mediaQueryMatch = _this$props.mediaQueryMatch,
      inverseColor = _this$props.inverseColor;
    return _jsx(Responsive, {
      "data-cid": "TopNavBar",
      elementRef: this.handleRef,
      match: mediaQueryMatch,
      query: {
        smallViewport: {
          maxWidth: this.breakpoint - 1
        },
        desktop: {
          minWidth: this.breakpoint
        }
      },
      render: (_props, matches) => {
        const layout = matches ? matches[0] : 'smallViewport';
        const isInverseColor = typeof inverseColor === 'function' ? inverseColor(layout) : !!inverseColor;
        const content = children({
          currentLayout: layout,
          inverseColor: isInverseColor
        });
        if (!matchComponentTypes(content, [TopNavBarLayout])) {
          error(false, 'The `children` function prop of TopNavBar has to return a child of type <TopNavBar.Layout>, but it returned:', content);
        }
        return _jsx(TopNavBarContext.Provider, {
          value: {
            layout,
            inverseColor: isInverseColor
          },
          children: content
        });
      }
    });
  }
}
TopNavBar.displayName = "TopNavBar";
TopNavBar.componentId = 'TopNavBar';
// TODO: mention subcomponent registration in "new component" docs
TopNavBar.ActionItems = TopNavBarActionItems;
TopNavBar.Brand = TopNavBarBrand;
TopNavBar.Item = TopNavBarItem;
TopNavBar.Layout = TopNavBarLayout;
TopNavBar.MenuItems = TopNavBarMenuItems;
TopNavBar.User = TopNavBarUser;
TopNavBar.Breadcrumb = TopNavBarBreadcrumb;
TopNavBar.contextType = TopNavBarContext;
TopNavBar.allowedProps = allowedProps;
TopNavBar.defaultProps = {
  breakpoint: 1024,
  mediaQueryMatch: 'media',
  inverseColor: false
};
export { TopNavBar };
export default TopNavBar;